'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import queryString from 'query-string';

export type RouterQuery = Record<string, string | string[] | undefined>;

function searchParamsToQuery(searchParams: URLSearchParams): RouterQuery {
    const query: RouterQuery = {};

    searchParams.forEach((value, key) => {
        const existing = query[key];
        if (existing !== undefined) {
            query[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
        } else {
            query[key] = value;
        }
    });

    return query;
}

/**
 * Build a URL with query string. Safe to use from App Router pages (no `next/router` context required).
 */
export function formatUrlWithQuery(url: string, queries: RouterQuery): string {
    if (queries && Object.entries(queries).length) {
        return `${url}?${queryString.stringify(queries, {
            arrayFormat: 'bracket'
        })}`;
    }
    return url;
}

const useRouterQuery = () => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const deleteQuery = (query: string | string[] = '') => {
        const queries = searchParamsToQuery(searchParams);

        if (typeof query === 'string') {
            delete queries[query];
        } else if (Array.isArray(query)) {
            query.forEach((item: string) => {
                delete queries[item];
            });
        }

        return formatUrlWithQuery(pathname, queries);
    };

    const deleteAllQueries = (_path?: string) => {
        return pathname;
    };

    const addQueryParams = (params: string[]) => {
        const formattedQuery = params.map((param) => `?${param}`);
        router.push(`${pathname}${formattedQuery.join('')}`);
    };

    const clearUrlQueriesWithoutReload = () => {
        router.replace(pathname);
    };

    return {
        formatUrlWithQuery,
        deleteQuery,
        deleteAllQueries,
        addQueryParams,
        clearUrlQueriesWithoutReload
    };
};

export default useRouterQuery;
